import CTNButton from '@ctn/ui/components/ctn-button';
import CTNMessage from '@ctn/ui/components/ctn-message';
import { useNotification } from '@ctn/ui/components/ctn-notifications';
import { useMemo, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import IntervinientesListado from '@/components/IntervinientesListado';
import Interviniente from '@/components/ModalInterviniente';
import { useGlobalContext } from '@/context/global.context';

import { getAllEntitiesAdicionals, getAllEntitiesRequireds } from '@/helpers/formSolicitudes';
import { notificationsConfig } from '@/helpers/notifications';
import { type FormValues, type ISujetoListado } from '@/interfaces/model/don.interface';
import { findID } from '@/utils/id';
import { getFullName } from '@/utils/string/string';

import IntervinienteGroup from '../../../components/IntervinienteGroup';

const IntervinientesSection = () => {
	const [openInterviniente, setOpenInterviniente] = useState(false);
	const [editInterviente, setEditInterviente] = useState<ISujetoListado>();
	const { watch, control } = useFormContext<FormValues>();
	const { openNotification, closeNotification } = useNotification();
	const notificationTemplate = notificationsConfig({ closeNotification });
	const {
		maestrosTramite: { tipoIntervencion },
	} = useGlobalContext();

	const { append, remove, update } = useFieldArray({
		control,
		name: 'sujetosAdicionales',
	});

	const controlArray = useFieldArray({
		control,
		name: 'sujetosRequeridos',
	});

	const { sujetosAdicionales } = watch();

	const allEntitiesRequireds = getAllEntitiesRequireds(tipoIntervencion, true);

	const filterIntervencionAdicional = useMemo(
		() => getAllEntitiesAdicionals(tipoIntervencion).map(({ id }) => id),
		[tipoIntervencion],
	);

	const onEditAdicionales = (data: ISujetoListado) => {
		setEditInterviente(data);
		setOpenInterviniente(true);
	};

	const onCloseAdicional = () => {
		setOpenInterviniente(false);
		setEditInterviente(undefined);
	};

	const onDeleteAdicionales = ({
		id,
		idReact,
		numeroDocumento,
		tipoIntervencion,
		nombre,
		apellido1RacSoc,
		apellido2,
	}: ISujetoListado) => {
		const index = findID({ list: sujetosAdicionales, idReact, id });
		const fullName = getFullName(nombre, apellido1RacSoc, apellido2);

		const modalSuccesEliminarDocument = () => {
			openNotification(notificationTemplate.successMessage(
				'Interviniente eliminado',
				'Se ha eliminado correctamente al interviniente.'
			));
		};

		openNotification(
			notificationTemplate.removeItem({
				title: 'Eliminar interviniente',
				content: `¿Eliminar ${tipoIntervencion.descripcion} - ${numeroDocumento} - ${fullName}?`,
				onAccept() {
					setTimeout(() => {
						modalSuccesEliminarDocument();
					}, 300);
					remove(index);
				},
			}),
		);
	};

	const onSaveAdicionales = (value: ISujetoListado) => {
		const index = findID({ list: sujetosAdicionales, idReact: value.idReact, id: value.id });
		if (index === -1) {
			append(value);
		} else {
			update(index, value);
		}
	};

	return (
		<>
			{allEntitiesRequireds.length > 0 && (
				<CTNMessage
					content={
						<div>
							<p>Para poder continuar, debes añadir al menos los siguientes intervinientes:</p>
							<ul>
								{allEntitiesRequireds.map(({ descripcion, id, minimoObligatorio }) => (
									<li key={id}>
										<p>
											· {descripcion}{' '}
											{`(${minimoObligatorio} ${minimoObligatorio === 1 ? 'Interviniente' : 'Intervinientes'})`}
											.
										</p>
									</li>
								))}
							</ul>
						</div>
					}
					icon="warning"
					status="warning"
					mode="outline"
					closable={false}
				/>
			)}
			{controlArray.fields.length > 0 && (
				<fieldset className="u-mt-24">
					<legend className="c-pnp-heading-h4">Intervinientes requeridos</legend>
					{controlArray.fields.map((field, index) => (
						<IntervinienteGroup key={field.id} name={`sujetosRequeridos.${index}`} />
					))}
				</fieldset>
			)}
			<section className="u-mt-32">
				<header className="o-splitter o-splitter--responsive">
					<h4 className="c-pnp-heading-h4">Intervinientes adicionales</h4>
					<CTNButton
						mode="secondary"
						label="Añadir interviniente adicional"
						variant="outline"
						minWidth="140"
						size="size-40"
						onClick={() => setOpenInterviniente(true)}
					/>
				</header>
				<div className="u-mt-24 u-mt-16@portrait">
					<IntervinientesListado
						data={sujetosAdicionales}
						noData="Aún no has añadido ningún interviniente adicional"
						onDelete={onDeleteAdicionales}
						onEdit={onEditAdicionales}
					/>
				</div>
				<Interviniente
					type="interviniente adicional"
					isOpen={openInterviniente}
					data={editInterviente}
					filterIntervencion={filterIntervencionAdicional}
					onClose={onCloseAdicional}
					onSave={onSaveAdicionales}
				/>
			</section>
		</>
	);
};

export default IntervinientesSection;
